import { computed, ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { usePracticeStore } from '@/stores';

export type FiresheetVersion = 2 | 3;

/**
 * Practice.firesheetBeta is inverted: true pins the practice to the legacy FS2
 * engine, anything else gets FS3. This is the only place that inversion lives —
 * see window.dxFsEngine in dxcloud.webui for the Angular twin.
 *
 * An unloaded practice means FS2, and the test is the id, not the flag: the store
 * seeds currentPractice as an empty object, which is truthy but carries no flag, so
 * a flag-only check would read "not loaded" as "FS3" and boot the wrong engine.
 */
export const versionForPractice = (practice?: { id?: string; firesheetBeta?: boolean } | null): FiresheetVersion => {
  if (!practice?.id) return 2;
  return practice.firesheetBeta ? 2 : 3;
};

/**
 * Which Firesheet engine this browser should run. Reads the practice directly —
 * the Vue app is a cross-origin iframe and cannot see webui's fsxV storage.
 */
export const useFiresheetEngine = (): {
  version: ComputedRef<FiresheetVersion>;
  isV2: ComputedRef<boolean>;
  isV3: ComputedRef<boolean>;
} => {
  const { currentPractice } = storeToRefs(usePracticeStore());

  const version = computed(() => versionForPractice(currentPractice.value));

  return {
    version,
    isV2: computed(() => version.value === 2),
    isV3: computed(() => version.value === 3)
  };
};
